import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Bell, X} from 'lucide-react';
import {staffApi} from '@/lib/staffApi';

export function StaffUpdatesBell() {
    const [updates, setUpdates] = useState([]);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const button = useRef(null), close = useRef(null), panel = useRef(null);
    const alive = useRef(true), inFlight = useRef(false);
    const load = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        try {
            const result = await staffApi.staffUpdates();
            if (alive.current) {setUpdates(result.updates || []); setError('');}
        } catch {if (alive.current) setError('Updates could not be loaded. Please try again.');}
        finally {inFlight.current = false;}
    };
    useEffect(() => {
        alive.current = true; load();
        const focus = () => {if (document.visibilityState === 'visible') load();};
        window.addEventListener('focus', focus);
        const timer = setInterval(focus, 300000);
        return () => {alive.current = false; clearInterval(timer); window.removeEventListener('focus', focus);};
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!open) return;
        close.current?.focus();
        const key = e => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Tab') {
                const nodes = [...panel.current.querySelectorAll('button:not(:disabled), a[href]')];
                if (e.shiftKey && document.activeElement === nodes[0]) {e.preventDefault(); nodes.at(-1)?.focus();}
                else if (!e.shiftKey && document.activeElement === nodes.at(-1)) {e.preventDefault(); nodes[0]?.focus();}
            }
        };
        document.addEventListener('keydown', key);
        return () => {document.removeEventListener('keydown', key); button.current?.focus();};
    }, [open]);
    const unread = updates.filter(note => !note.read);
    const markRead = async () => {
        setBusy(true);
        const ids = unread.map(note => note.id);
        try {
            await staffApi.readStaffUpdates(ids);
            setUpdates(rows => rows.map(row => ids.includes(row.id) ? {...row, read:true} : row));
            setError('');
        } catch {setError('Could not save your read status. Please try again.');}
        finally {setBusy(false);}
    };
    return <>
        <button ref={button} type="button" onClick={() => {setOpen(true); load();}} aria-label={`App updates${unread.length ? `, ${unread.length} unread` : ''}`} aria-haspopup="dialog" aria-expanded={open}
            className="relative h-11 w-11 shrink-0 rounded-full border border-gold/40 grid place-items-center text-gold hover:bg-gold/10">
            <Bell className="h-5 w-5"/>
            {unread.length > 0 && <span className="absolute top-0 right-0 min-w-4 h-4 px-1 rounded-full bg-gold text-navy text-[10px] font-bold">{unread.length}</span>}
        </button>
        {open && createPortal(<div className="app-light fixed inset-0 z-[70] bg-black/50 flex justify-end" onClick={() => setOpen(false)}>
            <section ref={panel} role="dialog" aria-modal="true" aria-label="App updates" onClick={e => e.stopPropagation()} className="surface-highlight w-full max-w-md bg-[#101936] text-white p-5 overflow-y-auto" style={{paddingTop:'max(20px, env(safe-area-inset-top))',paddingBottom:'max(20px, env(safe-area-inset-bottom))'}}>
                <div className="flex justify-between items-center gap-4"><h2 className="font-serif text-3xl text-white">What’s new</h2><button ref={close} onClick={() => setOpen(false)} aria-label="Close updates" className="h-11 w-11 grid place-items-center text-white"><X/></button></div>
                {error && <p role="alert" className="my-4 text-amber-200">{error} <button className="underline" onClick={load}>Retry</button></p>}
                {!error && !updates.length && <p className="py-6 text-white/70">No updates yet.</p>}
                {updates.map(note => <article key={note.id} className="my-4 rounded-2xl border border-gold/30 p-4">
                    <div className="text-xs text-gold">{note.date}{!note.read && ' · New'}</div><h3 className="mt-2 font-semibold text-white">{note.title}</h3>
                    <ul className="list-disc pl-5 mt-3 space-y-2 text-sm text-white/80">{note.items.map(item => <li key={item}>{item}</li>)}</ul>
                </article>)}
                {unread.length > 0 && <button disabled={busy} onClick={markRead} className="rounded-full bg-gold text-navy h-11 px-5 font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Mark all as read'}</button>}
            </section>
        </div>, document.body)}
    </>;
}
